import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RecruiterSkeleton, ErrorState } from "@/components/shared";
import { cn } from "@/lib/utils";
import { Sparkles, ArrowRight } from "lucide-react";

const STAGE_LABEL = { applied: "Applied", screening: "Screening", interview: "Interview", offer: "Offer", hired: "Hired" };

const Kpi = ({ label, value, sub, subTone, testId }) => (
  <div className="hs-card px-5 py-4 min-w-0" data-testid={testId}>
    <div className="text-[13px] text-muted-foreground">{label}</div>
    <div className="text-[30px] hs-mono font-semibold leading-tight mt-1">{value}</div>
    {sub && <div className={cn("text-xs mt-0.5", subTone === "green" ? "text-primary" : "text-muted-foreground")}>{sub}</div>}
  </div>
);
const Card = ({ title, right, children, testId, className }) => (
  <section className={cn("hs-card p-5 sm:p-6 min-w-0", className)} data-testid={testId}>
    <div className="flex items-center justify-between gap-3 mb-5"><h2 className="text-[15px] font-semibold">{title}</h2>{right}</div>
    {children}
  </section>
);

function Fairness({ data }) {
  const max = data?.max_shortlist_rate || 0;
  const rows = (data?.rows || []).filter((r) => !r.insufficient_sample).slice(0, 4);
  if (!data) return <p className="text-sm text-muted-foreground">Loading fairness ratios…</p>;
  if (!rows.length || !max) return <p className="text-sm text-muted-foreground" data-testid="fairness-insufficient">Not enough scored candidates yet — each role needs at least {data.min_scored || 5} before a ratio is meaningful.</p>;
  return (
    <div className="space-y-3">
      {rows.map((g) => {
        const ratio = g.shortlist_rate / max;
        return (
          <div key={g.job_id} className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)_44px] items-center gap-4 text-sm">
            <span className="text-muted-foreground truncate">{g.title}</span>
            <span className="relative h-1.5 rounded-full bg-foreground/[0.08]"><span className={cn("absolute inset-y-0 left-0 rounded-full", g.adverse_impact ? "bg-amber-400" : "bg-primary")} style={{ width: `${Math.min(100, ratio * 100)}%` }} /><span className="absolute -top-1 -bottom-1 w-px bg-foreground/40" style={{ left: "80%" }} /></span>
            <span className="hs-mono text-right">{ratio.toFixed(2)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Analytics() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [brief, setBrief] = useState(null);
  const [bias, setBias] = useState(null);
  const [err, setErr] = useState(null);
  const gov = ["admin", "super_admin"].includes(user?.role) || user?.org_role === "owner";

  const load = () => {
    setErr(null); setData(null);
    Promise.all([api.get("/dashboard/analytics"), api.get("/dashboard/brief").catch(() => ({ data: null }))])
      .then(([a, b]) => { setData(a.data); setBrief(b.data); })
      .catch((e) => setErr(e.message));
    if (gov) api.get("/admin/bias").then((r) => setBias(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const header = (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div><h1 className="text-3xl sm:text-[32px] font-display font-semibold tracking-tight" data-testid="analytics-title">Insights</h1><p className="text-[15px] text-muted-foreground mt-1">All your roles · last 6 weeks</p></div>
      <button onClick={() => nav("/recruiter/pipeline")} className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="funnel-open-pipeline">Open candidates <ArrowRight className="h-3.5 w-3.5" /></button>
    </header>
  );
  if (err) return <div className="recruiter-page space-y-6" data-testid="analytics-page">{header}<ErrorState message={err} onRetry={load} /></div>;
  if (!data) return <div className="recruiter-page space-y-6" data-testid="analytics-page">{header}<RecruiterSkeleton variant="analytics" label="Crunching your hiring data…" /></div>;

  const f = Object.fromEntries(data.funnel.map((x) => [x.stage, x.count]));
  const maxF = Math.max(...data.funnel.map((x) => x.count), 1);
  const maxT = Math.max(...data.trend.map((t) => t.count), 1);
  const maxR = Math.max(...data.top_roles.map((r) => r.count), 1);
  const thisWeek = data.trend[data.trend.length - 1]?.count || 0;
  const empty = data.totals.candidates === 0;

  return (
    <div className="recruiter-page space-y-6" data-testid="analytics-page">
      {header}
      {brief?.headline && (
        <div className="flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/[0.06] px-5 py-3.5 text-sm" data-testid="analytics-brief">
          <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" /><p><strong className="font-semibold">Today:</strong> {brief.headline}</p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 hs-stagger" data-testid="analytics-kpis">
        <Kpi label="Applicants" value={data.totals.candidates} sub={thisWeek ? `+${thisWeek} this week` : "none this week"} subTone={thisWeek ? "green" : undefined} testId="kpi-candidates" />
        <Kpi label="Avg. time to review" value={data.time.avg_days_to_review != null ? `${data.time.avg_days_to_review}d` : "—"} sub={`${data.time.reviewed_count || 0} reviewed`} testId="kpi-avg-days-to-review" />
        <Kpi label="Avg. fit score" value={data.avg_score ?? "—"} sub="across scored applicants" testId="kpi-avg-fit-score" />
        <Kpi label="Offers / hired" value={`${f.offer || 0} / ${data.totals.hired}`} sub={data.time.avg_days_to_hire != null ? `${data.time.avg_days_to_hire}d avg to hire` : `${data.totals.rejected} not progressing`} testId="kpi-avg-days-to-hire" />
      </div>
      {empty ? (
        <div className="hs-card px-6 py-12 text-center space-y-2" data-testid="analytics-empty"><h2 className="font-semibold">Insights fill in as candidates apply</h2><p className="text-sm text-muted-foreground max-w-md mx-auto">Publish a role and share its link. Drop-off, weekly volume and fairness checks appear here once there&apos;s real data — we never show zeros as a trend.</p></div>
      ) : <>
        <div className="grid lg:grid-cols-[1.45fr_1fr] gap-5">
          <Card title="Where candidates drop off" testId="analytics-funnel">
            <div className="space-y-3">
              {data.funnel.map((x, i) => (
                <div key={x.stage} className="grid grid-cols-[84px_minmax(0,1fr)_76px] items-center gap-4" data-testid={`funnel-row-${x.stage}`}>
                  <span className="text-sm text-muted-foreground">{STAGE_LABEL[x.stage]}</span>
                  <span className="h-7 rounded-md bg-foreground/[0.05] overflow-hidden"><span className="block h-full rounded-md bg-gradient-to-r from-primary to-primary/60 transition-[width] duration-700" style={{ width: `${Math.max(x.count ? 3 : 0, (x.count / maxF) * 100)}%` }} /></span>
                  <span className="text-right text-sm"><span className="hs-mono font-semibold">{x.count}</span>{i > 0 && <span className="text-xs text-muted-foreground ml-1.5">{x.conversion}%</span>}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Applications per week" testId="analytics-trend">
            <div className="grid grid-cols-6 gap-2.5 items-end h-48">
              {data.trend.map((t, i) => {
                const last = i === data.trend.length - 1;
                return (
                  <div key={t.label} className="flex flex-col items-center justify-end h-full gap-1.5" data-testid={`trend-bar-${i}`} aria-label={`${t.label}: ${t.count} applications`}>
                    <span className="text-xs hs-mono" data-testid={`trend-count-${i}`}>{t.count}</span>
                    <span className={cn("w-full rounded-t-md transition-[height] duration-700", last ? "bg-primary" : "bg-primary/35")} style={{ height: `${Math.max(4, (t.count / maxT) * 78)}%` }} data-testid={`trend-fill-${i}`} />
                    <span className="text-[11px] text-muted-foreground">{t.label.replace(" ago", "")}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Roles by volume" testId="analytics-top-roles">
            <div className="space-y-3">
              {data.top_roles.map((r, i) => (
                <div key={r.title} className="grid grid-cols-[minmax(0,180px)_minmax(0,1fr)_36px] items-center gap-4 text-sm" data-testid={`top-role-${i}`}>
                  <span className="truncate">{r.title}</span>
                  <span className="h-1.5 rounded-full bg-foreground/[0.08]"><span className="block h-full rounded-full bg-sky-400" style={{ width: `${(r.count / maxR) * 100}%` }} /></span>
                  <span className="hs-mono text-right">{r.count}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Fairness check" testId="analytics-fairness" right={gov && <button onClick={() => nav("/admin/bias")} className="text-xs text-primary hover:underline">Open fairness report</button>}>
            <p className="text-[13px] text-muted-foreground mb-4">Shortlist-rate ratio per role against your best-performing role (four-fifths rule). Advisory — it never blocks a decision.</p>
            {gov ? <Fairness data={bias} /> : <p className="text-sm text-muted-foreground">Your organisation owner can review fairness ratios in Governance.</p>}
          </Card>
        </div>
      </>}
    </div>
  );
}
